'use client';

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

type Language = 'tr' | 'en';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const translations: Record<Language, Record<string, string>> = {
  tr: {
    // Navigation
    'nav.home': 'Ana Sayfa',
    'nav.about': 'Hakkımızda',
    'nav.products': 'Ürünler',
    'nav.production': 'Üretim',
    'nav.wholesale': 'Toptan Satış',
    'nav.contact': 'İletişim',
    'nav.contactUs': 'Bize Ulaşın',
    'nav.coffeetainer': 'Coffeetainer',
    
    // Common
    'common.learnMore': 'Daha Fazla Bilgi',
    'common.getStarted': 'Başlayın',
    'common.readMore': 'Devamını Oku',
    'common.viewAll': 'Tümünü Görüntüle',
    'common.submit': 'Gönder',
    'common.cancel': 'İptal',
    'common.search': 'Ara',
    'common.filter': 'Filtrele',
    'common.sort': 'Sırala',
    
    // Homepage - Hero Section
    'home.hero.slide1.title': 'Toptan Ölçekte Premium Kalite',
    'home.hero.slide1.subtitle': '2016\'dan beri güvenilir ortağınız',
    'home.hero.slide1.cta': 'Ürünleri Keşfet',
    'home.hero.slide2.title': 'Gelenek ve Verimlilik Buluşuyor',
    'home.hero.slide2.subtitle': 'Ticari başarı için el yapımı kalite',
    'home.hero.slide2.cta': 'Üretimi Görüntüle',
    'home.hero.slide3.title': 'Toplu Siparişler Çok Kolay',
    'home.hero.slide3.subtitle': 'İşletmeniz için düzenlenmiş toptan sipariş',
    'home.hero.slide3.cta': 'Toptan Sipariş Ver',
    
    // Homepage - Trust Indicators
    'home.trust.years': 'Yıllık Mükemmellik',
    'home.trust.partners': 'İş Ortağı',
    'home.trust.deliveries': 'Aylık Teslimat',
    'home.trust.guaranteed': 'Kalite Garantisi',
    
    // Homepage - Quick Access
    'home.quickAccess.title': 'Kaliteye Açılan Kapınız',
    'home.quickAccess.subtitle': 'Sidem Gıda\'nın premium toptan ürünlerle işinizi nasıl yükselteceğini keşfedin',
    'home.quickAccess.products': 'Ürünlerimiz',
    'home.quickAccess.products.desc': 'Premium fırın ürün yelpazemizi keşfedin',
    'home.quickAccess.production': 'Üretim Tesisi',
    'home.quickAccess.production.desc': 'Son teknoloji üretim sürecimizi görün',
    'home.quickAccess.wholesale': 'Toptan Siparişler',
    'home.quickAccess.wholesale.desc': 'İşletmeniz için toplu sipariş verin',
    'home.quickAccess.about': 'Sidem Hakkında',
    'home.quickAccess.about.desc': 'Mirasımız ve değerlerimiz hakkında bilgi edinin',
    
    // Homepage - Featured Products
    'home.featured.title': 'Öne Çıkan Ürünler',
    'home.featured.subtitle': 'Mevsimlik tekliflerimizi ve çok satanlarımızı keşfedin',
    'home.featured.product1.name': 'El Yapımı Ekşi Maya Ekmeği',
    'home.featured.product1.category': 'Ekmekler',
    'home.featured.product1.desc': '48 saatlik fermantasyonla geleneksel ekşi maya',
    'home.featured.product1.badge': 'Çok Satan',
    'home.featured.product2.name': 'Tereyağlı Kruvasanlar',
    'home.featured.product2.category': 'Hamur İşleri',
    'home.featured.product2.desc': 'Premium Fransız tereyağı ile katmanlı mükemmellik',
    'home.featured.product2.badge': 'Yeni',
    'home.featured.product3.name': 'Tam Tahıllı Çörekler',
    'home.featured.product3.category': 'Ekmekler',
    'home.featured.product3.desc': 'Restoranlar için mükemmel besleyici çörekler',
    'home.featured.product3.badge': 'Mevsimlik',
    'home.featured.product4.name': 'Türk Simiti',
    'home.featured.product4.category': 'Özel Ürünler',
    'home.featured.product4.desc': 'Otantik susamlı Türk simit',
    'home.featured.product4.badge': 'Geleneksel',
    'home.featured.viewAll': 'Tüm Ürünleri Görüntüle',
    
    // Homepage - Testimonials
    'home.testimonials.title': 'Ortaklarımız Ne Diyor',
    'home.testimonials.subtitle': 'Türkiye genelindeki işletmelerin güvendiği',
    'home.testimonials.client1.quote': 'Sidem Gıda 5 yıldır güvenilir ortağımız. Tutarlı kaliteleri ve güvenilir teslimatları, kafemizin mükemmellik itibarını korumamıza yardımcı oldu.',
    'home.testimonials.client1.name': 'Mehmet Yılmaz',
    'home.testimonials.client1.position': 'Sahip',
    'home.testimonials.client1.company': 'Café Istanbul',
    'home.testimonials.client2.quote': 'Toptan sipariş süreci kusursuz ve ürün kalitesi beklentilerimizi aşıyor. Misafirlerimiz kahvaltı tekliflerimizi sürekli övüyor.',
    'home.testimonials.client2.name': 'Ayşe Demir',
    'home.testimonials.client2.position': 'Satın Alma Müdürü',
    'home.testimonials.client2.company': 'Grand Hotel Ankara',
    'home.testimonials.client3.quote': 'Bir şef olarak en iyi malzemeleri talep ediyorum. Sidem, toptan ölçekte el yapımı kalite sunuyor—fine dining işletmemiz için tam ihtiyacımız olan şey.',
    'home.testimonials.client3.name': 'Can Özdemir',
    'home.testimonials.client3.position': 'Baş Şef',
    'home.testimonials.client3.company': 'Restaurant Bosphorus',
    
    // Homepage - CTA Section
    'home.cta.title': 'Sidem Gıda ile Ortaklığa Hazır mısınız?',
    'home.cta.subtitle': 'Toptan fırın ihtiyaçları için bize güvenen yüzlerce işletmeye katılın',
    'home.cta.wholesale': 'Toptan Sipariş Ver',
    'home.cta.contact': 'İletişime Geç',
    'home.cta.quality': 'Premium Kalite',
    'home.cta.delivery': 'Güvenilir Teslimat',
    'home.cta.pricing': 'Rekabetçi Fiyatlandırma',
    
    // Homepage - Footer
    'footer.company.title': 'Şirket',
    'footer.products.title': 'Ürünler',
    'footer.contact.title': 'İletişim Bilgileri',
    'footer.about': 'Hakkımızda',
    'footer.production': 'Üretim',
    'footer.contact': 'İletişim',
    'footer.allProducts': 'Tüm Ürünler',
    'footer.wholesale': 'Toptan Satış',
    'footer.location': 'İstanbul, Türkiye',
    'footer.phone': '+90 (212) 555-0123',
    'footer.email': 'info@sidembakery.com',
    'footer.description': '2016\'dan beri premium toptan fırın tedarikçisi. Gelenek ve verimlilik buluşuyor.',
    'footer.rights': 'Tüm hakları saklıdır.',
    'footer.privacy': 'Gizlilik Politikası',
    'footer.terms': 'Hizmet Şartları',
    
    // About/Production Footer
    'footer.section.company': 'Şirket',
    'footer.section.support': 'Destek',
    'footer.section.legal': 'Yasal',
    'footer.link.aboutUs': 'Hakkımızda',
    'footer.link.products': 'Ürünler',
    'footer.link.production': 'Üretim',
    'footer.link.wholesale': 'Toptan Satış',
    'footer.link.contactUs': 'Bize Ulaşın',
    'footer.link.faq': 'SSS',
    'footer.link.orderTracking': 'Sipariş Takibi',
    'footer.link.privacyPolicy': 'Gizlilik Politikası',
    'footer.link.termsOfService': 'Hizmet Şartları',
    'footer.link.qualityStandards': 'Kalite Standartları',
    'footer.tagline': '2016\'dan beri premium toptan fırın tedarikçisi. Gelenek ve verimlilik buluşuyor.',
    'footer.allRights': 'Tüm hakları saklıdır.',
    'footer.craftedIn': 'Türkiye\'de özenle hazırlanmıştır',
    
    // Production Page
    'production.content.paragraph1': 'Sidem Gıda olarak üretim süreçlerimizi; tazelik, hijyen, kalite, lezzet ve ileri teknoloji prensipleri üzerine kuruyoruz. Modern üretim hattımızda kullanılan en yeni ekipmanlar, her aşamada standartları yukarıya taşırken; deneyimli ustalarımızın profesyonelliği ürünlerimize benzersiz bir karakter katıyor.',
    'production.content.paragraph2': 'Ham madde seçiminden pişirme ve son kontrole kadar tüm adımlar titizlikle takip edilir. Hijyenik üretim alanlarımızda, gıda güvenliği protokollerine uygun şekilde çalışır; her ürünün aynı yüksek standartta olmasını garanti ederiz. Ustalarımızın yıllara dayanan tecrübesi ile teknolojiyi birleştirerek, müşterilerimize daima taze, güvenilir ve lezzetiyle fark yaratan unlu mamuller sunuyoruz.',
    'production.content.paragraph3': 'Sidem Gıda, Türkiye\'nin önde gelen markalarına tedarik sağlayan üretim gücünü; kaliteye olan tutkusundan ve sürekli yenilenen modern tesisinden alır.',
    
    // Contact Page
    'contact.hero.title': 'Bize Ulaşın',
    'contact.hero.subtitle': 'Sorularınız, talepleriniz ve önerileriniz için her zaman yanınızdayız',
    'contact.info.phone': 'Telefon',
    'contact.info.email': 'E-posta',
    'contact.info.address': 'Adres',
    'contact.info.whatsapp': 'WhatsApp',
    'contact.info.whatsapp.support': 'Hızlı destek için',
    'contact.form.title': 'Bize Mesaj Gönderin',
    'contact.form.subtitle': 'Formu doldurun, en kısa sürede size geri dönelim',
    'contact.form.type': 'Talep Türü',
    'contact.form.type.placeholder': 'Lütfen seçiniz',
    'contact.form.type.wholesale': 'Toptan Satış Talebi',
    'contact.form.type.partnership': 'Ortaklık ve İşbirliği',
    'contact.form.type.technical': 'Teknik Destek',
    'contact.form.type.general': 'Genel Bilgi',
    'contact.form.type.complaint': 'Şikayet ve Öneri',
    'contact.form.type.quality': 'Kalite ve Sertifikasyon',
    'contact.form.name': 'Ad Soyad',
    'contact.form.email': 'E-posta',
    'contact.form.phone': 'Telefon',
    'contact.form.company': 'Firma Adı',
    'contact.form.message': 'Mesajınız',
    'contact.form.submit': 'Mesaj Gönder',
    'contact.map.title': 'COFFEETAINER',
    'contact.map.viewOnMaps': 'Google Maps\'te Aç',
    'contact.map.directions': 'Yol Tarifi Al',
    'contact.hours.title': 'Çalışma Saatleri',
    'contact.hours.monday': 'Pazartesi',
    'contact.hours.tuesday': 'Salı',
    'contact.hours.wednesday': 'Çarşamba',
    'contact.hours.thursday': 'Perşembe',
    'contact.hours.friday': 'Cuma',
    'contact.hours.saturday': 'Cumartesi',
    'contact.hours.sunday': 'Pazar',
    'contact.hours.closed': 'Kapalı',
    
    // Wholesale Form
    'wholesale.form.title': 'Toptan Teklif Talebi',
    'wholesale.form.subtitle': 'Aşağıdaki formu doldurun, ekibimiz 24 saat içinde size geri dönecek',
    'wholesale.form.companyName': 'Şirket Adı',
    'wholesale.form.companyName.placeholder': 'Şirket Adınız',
    'wholesale.form.contactPerson': 'İletişim Kişisi',
    'wholesale.form.contactPerson.placeholder': 'Ad Soyad',
    'wholesale.form.email': 'E-posta Adresi',
    'wholesale.form.email.placeholder': 'sirket@ornek.com',
    'wholesale.form.phone': 'Telefon Numarası',
    'wholesale.form.phone.placeholder': '+90 XXX XXX XX XX',
    'wholesale.form.businessType': 'İşletme Türü',
    'wholesale.form.businessType.placeholder': 'İşletme türünüzü seçin',
    'wholesale.form.businessType.restaurant': 'Restoran',
    'wholesale.form.businessType.cafe': 'Kafe/Kahve Dükkanı',
    'wholesale.form.businessType.hotel': 'Otel',
    'wholesale.form.businessType.catering': 'Catering Şirketi',
    'wholesale.form.businessType.bakery': 'Fırın/Pasta Dükkanı',
    'wholesale.form.businessType.distributor': 'Gıda Distribütörü',
    'wholesale.form.businessType.supermarket': 'Süpermarket Zinciri',
    'wholesale.form.businessType.other': 'Diğer',
    'wholesale.form.productType': 'Ürün Türü',
    'wholesale.form.productType.placeholder': 'Ürün türünü seçin',
    'wholesale.form.productType.coffee': 'Kahveler',
    'wholesale.form.productType.bakery': 'Unlu mamüller',
    'wholesale.form.productInterest': 'Ürün İlgi Alanı',
    'wholesale.form.productInterest.loading': 'Ürünler yükleniyor...',
    'wholesale.form.productInterest.none': 'Bu kategori için ürün bulunamadı',
    'wholesale.form.estimatedQuantity': 'Tahmini Miktar',
    'wholesale.form.estimatedQuantity.placeholder': 'Miktar aralığını seçin',
    'wholesale.form.unit.bakery': 'adet',
    'wholesale.form.unit.coffee': 'kg',
    'wholesale.form.deliveryFrequency': 'Teslimat Sıklığı',
    'wholesale.form.deliveryFrequency.placeholder': 'Teslimat sıklığını seçin',
    'wholesale.form.deliveryFrequency.daily': 'Günlük',
    'wholesale.form.deliveryFrequency.every2-3days': 'Her 2-3 günde bir',
    'wholesale.form.deliveryFrequency.weekly': 'Haftalık',
    'wholesale.form.deliveryFrequency.biweekly': 'İki haftada bir',
    'wholesale.form.deliveryFrequency.monthly': 'Aylık',
    'wholesale.form.preferredCurrency': 'Tercih Edilen Para Birimi',
    'wholesale.form.currency.try': 'Türk Lirası (₺)',
    'wholesale.form.currency.eur': 'Euro (€)',
    'wholesale.form.currency.usd': 'ABD Doları ($)',
    'wholesale.form.message': 'Ek Bilgiler',
    'wholesale.form.message.placeholder': 'Gereksinimleriniz, özel talepleriniz veya sorularınız hakkında bize daha fazla bilgi verin...',
    'wholesale.form.required': 'Zorunlu alanlar. Gizliliğinize saygı duyuyoruz ve bilgilerinizi asla paylaşmayacağız.',
    'wholesale.form.submit': 'Toptan Talep Gönder',
    'wholesale.form.submitting': 'Talep Gönderiliyor...',
    'wholesale.form.success.title': 'Talebiniz Başarıyla Gönderildi!',
    'wholesale.form.success.thankyou': 'İlginiz için teşekkür ederiz.',
    'wholesale.form.success.message': 'En kısa sürede size geri döneceğiz. Toptan satış ekibimiz talebinizi inceleyecek ve sizinle iletişime geçecek.',
    'wholesale.form.error.companyName': 'Şirket adı gereklidir',
    'wholesale.form.error.contactPerson': 'İletişim kişisi adı gereklidir',
    'wholesale.form.error.email.required': 'E-posta gereklidir',
    'wholesale.form.error.email.invalid': 'Lütfen geçerli bir e-posta adresi girin',
    'wholesale.form.error.phone.required': 'Telefon numarası gereklidir',
    'wholesale.form.error.phone.invalid': 'Lütfen geçerli bir telefon numarası girin',
    'wholesale.form.error.businessType': 'Lütfen işletme türünüzü seçin',
    'wholesale.form.error.productType': 'Lütfen ürün türünü seçin',
    'wholesale.form.error.productInterest': 'Lütfen en az bir ürün seçin',
    'wholesale.form.error.estimatedQuantity': 'Lütfen tahmini miktarı seçin',
    'wholesale.form.error.deliveryFrequency': 'Lütfen teslimat sıklığını seçin',
    
    // Wholesale Page - Hero Section
    'wholesale.hero.title': 'Toptan Ortaklık Programı',
    'wholesale.hero.subtitle': 'Toptan ölçekte premium kalite. Güvenilir tedarik, rekabetçi fiyatlandırma ve 2016\'dan beri olağanüstü hizmet için Sidem Fırın ile ortaklık kurun.',
    
    // Wholesale Page - Benefits Section
    'wholesale.benefits.title': 'Neden Toptan Satış için Sidem\'i Seçmelisiniz?',
    'wholesale.benefits.subtitle': '2016\'dan beri güvenilir hizmetle toptan ölçekte premium kalite',
    'wholesale.benefits.quality.title': 'Premium Kalite',
    'wholesale.benefits.quality.description': 'Sıkı kalite kontrol standartları ile tüm toptan hacimlerde tutarlı zanaat kalitesi.',
    'wholesale.benefits.delivery.title': 'Güvenilir Teslimat',
    'wholesale.benefits.delivery.description': 'Günlük ile aylık arası esnek teslimat programları, işletmenizin taze ürünlerden asla yoksun kalmamasını sağlar.',
    'wholesale.benefits.pricing.title': 'Rekabetçi Fiyatlandırma',
    'wholesale.benefits.pricing.description': 'Türk Lirası veya Euro\'da şeffaf fiyatlandırma, %25\'e kadar hacme dayalı indirimlerle.',
    'wholesale.benefits.support.title': 'Özel Destek',
    'wholesale.benefits.support.description': 'Tüm toptan ihtiyaçlarınızı verimli bir şekilde yönetmek için kişisel hesap yöneticisi ve 7/24 müşteri desteği.',
    
    // Wholesale Page - FAQ Section
    'wholesale.faq.title': 'Sıkça Sorulan Sorular',
    'wholesale.faq.subtitle': 'Toptan hizmetlerimiz hakkında sık sorulan sorulara cevaplar bulun',
    'wholesale.faq.q1.question': 'Toptan satış için minimum sipariş miktarı nedir?',
    'wholesale.faq.q1.answer': 'Minimum sipariş miktarımız Başlangıç paketi için haftada 100 birimden başlar. Ancak esneyiz ve özel iş ihtiyaçlarınıza ve büyüme planlarınıza göre özel düzenlemeler yapabiliriz.',
    'wholesale.faq.q2.question': 'Siparişleri ne kadar hızlı teslim edebilirsiniz?',
    'wholesale.faq.q2.answer': 'Teslimat programları, seçtiğiniz pakete ve konuma göre günlükten aylığa kadar değişir. İstanbul ve çevresinde, saat 10:00\'dan önce verilen siparişler için aynı gün teslimat sunuyoruz. Diğer bölgeler genellikle 24-48 saat içinde teslimat alır.',
    'wholesale.faq.q3.question': 'Özel ürün geliştirme hizmeti sunuyor musunuz?',
    'wholesale.faq.q3.answer': 'Evet! Kurumsal paketimiz özel ürün geliştirme hizmetlerini içerir. Marka gereksinimlerinize ve müşteri tercihlerinize uygun benzersiz fırın ürünleri oluşturmak için ekibinizle yakın çalışıyoruz.',
    'wholesale.faq.q4.question': 'Hangi ödeme koşullarını sunuyorsunuz?',
    'wholesale.faq.q4.answer': 'Ödeme koşulları pakete göre değişir: Başlangıç için 30 gün, Profesyonel için 45 gün ve Kurumsal için 60 gün. Banka havalesi, kredi kartı kabul ediyoruz ve uzun vadeli ortaklar için özel ödeme planları düzenleyebiliriz.',
    'wholesale.faq.q5.question': 'Toptan siparişe karar vermeden önce numune alabilir miyim?',
    'wholesale.faq.q5.answer': 'Kesinlikle! Tüm potansiyel toptan ortaklarımızı önce ürünlerimizi denemelerini teşvik ediyoruz. En popüler ürünlerimizin ücretsiz numune paketini ayarlamak için satış ekibimizle iletişime geçin.',
    'wholesale.faq.q6.question': 'Ürün kalite sorunlarını nasıl ele alıyorsunuz?',
    'wholesale.faq.q6.answer': '%100 memnuniyet garantimiz var. Herhangi bir ürün kalite standartlarımızı karşılamazsa, ücret ödemeden hemen değiştireceğiz. Kalite güvence ekibimiz üretim ve teslimat sürecinde düzenli kontroller yapar.',
    
    // Wholesale Page - CTA Section
    'wholesale.cta.title': 'Sidem Fırın ile Ortaklığa Hazır mısınız?',
    'wholesale.cta.subtitle': 'Premium kaliteli fırın ürünleri için bize güvenen yüzlerce memnun toptan müşteriye katılın. İşinizin büyümesini nasıl destekleyebileceğimizi konuşalım.',
    'wholesale.cta.contactSales': 'Satış Ekibiyle İletişime Geçin',
    'wholesale.cta.viewCatalog': 'Ürün Kataloğunu Görüntüle',
    
    // About Page - Hero Section
    'about.hero.title': 'Gelenek Mükemmellikle Buluşuyor',
    'about.hero.subtitle': 'Hikayemiz',
    'about.hero.description': 'Sidem Gıda, 2016 yılından bu yana unlu mamüller sektöründe kalite, hijyen ve lezzeti bir araya getiren butik üretim anlayışıyla hizmet vermektedir. Özellikle ustalık gerektiren Fransız kruvasanı, özel reçeteli brioche, taze günlük hamur işleri, cookie, poğaça ve pastane ürünleri ile işletmelere yüksek standartlarda çözümler sunuyoruz. Her ürünümüzü geleneksel tekniklerle, doğru hamur dinlendirme süreleriyle ve seçilmiş hammaddelerle hazırlıyor; müşterilerimize hem tazelik hem de sürdürülebilir kalite güvencesi sağlıyoruz. Sidem Gıda olarak, işletmelerin ihtiyaçlarına özel üretim yapıyor, toptan satışlarımızla Türkiye\'nin birçok noktasında profesyonel mutfakların güvenilir tedarikçisi olmaya devam ediyoruz.',
    
    // About Page - Timeline Section
    'about.timeline.title': 'Yolculuğumuz',
    'about.timeline.subtitle': 'Mükemmellik taahhüdümüzü şekillendiren kilometre taşları',
    'about.timeline.2016.title': 'Kuruluş',
    'about.timeline.2016.description': 'Sidem Gıda, küçük bir üretim tesisi ve kalite taahhüdü ile başlayarak, geleneksel Türk fırıncılık mükemmeliyetini toptan satış pazarına getirme vizyonuyla kuruldu.',
    'about.timeline.2018.title': 'Genişleme',
    'about.timeline.2018.description': 'Üretim kapasitemizi %300 artırdık ve zanaatkarlık yaklaşımımızı koruyarak otomatik kalite kontrol sistemleri getirdik.',
    'about.timeline.2020.title': 'Fabrika Dönüşümü',
    'about.timeline.2020.description': 'Coffeetainer markasını 2016 yılında kurduktan sonra, öncelikle kendi zincir şubelerimizin unlu mamul ihtiyacını karşılayan üretim atölyemiz; artan talep, kalite standartlarımız ve profesyonel üretim gücümüzle kısa sürede fabrikaya dönüşerek bugün Türkiye\'nin önde gelen markalarına toptan satış yapan güçlü bir tedarikçi haline gelmiştir.',
    'about.timeline.2022.title': 'İnovasyon',
    'about.timeline.2022.description': 'Dijital toptan satış platformumuzu başlattık ve sürdürülebilir ambalaj çözümleri getirerek sipariş vermeyi kolaylaştırırken çevreyi koruduk.',
    'about.timeline.2024.title': 'Bölgesel Lider',
    'about.timeline.2024.description': 'Türkiye genelinde restoranlar, oteller ve kafelere tutarlı mükemmellikle hizmet vererek işletmelerin tercih ettiği toptan fırın tedarikçisi olduk.',
    
    // About Page - Founder Section
    'about.founder.title': 'Kurucumuz',
    'about.founder.name': 'Ezgican Aydoğan',
    'about.founder.position': 'Kurucu & Girişimci',
    'about.founder.story.p1': 'Ezgican Aydoğan, horeca ve gıda sektöründe uzun yıllara dayanan tecrübesiyle Türkiye\'nin yenilikçi markalarından Coffeetainer ve Sidem Gıda\'nın kurucusudur. Kariyerine otelcilik sektöründe başlayan Aydoğan, misafir deneyimi, gastronomi yönetimi ve operasyonel süreçler üzerine edindiği deneyimi girişimcilik vizyonuyla birleştirerek kendi markalarını oluşturma yoluna girmiştir.',
    'about.founder.story.p2': '2016 yılında kurduğu Coffeetainer, üçüncü nesil kahve kültürünü konteyner mimarisi ve modern tasarım anlayışıyla birleştirerek kısa sürede geniş bir kitleye ulaşmış; Antalya, Ankara, Manisa ve Bakü gibi birçok şehirde şubeleriyle güçlü bir marka olarak konumlanmıştır. Aydoğan\'ın tasarım odaklı yaklaşımı sayesinde Coffeetainer yalnızca bir kahve zinciri değil, aynı zamanda bir yaşam stili ve deneyim markası haline gelmiştir.',
    'about.founder.story.p3': 'Coffeetainer\'ın büyümesiyle paralel olarak kurulan Sidem Gıda, başlangıçta markanın kendi şubelerine unlu mamul üretimi sağlamak için kurulmuş bir atölyeyken, kısa sürede fabrika ölçeğine ulaşarak Türkiye\'nin önde gelen kahve zincirleri ve markalarına toptan kruvasan ve özel üretim unlu mamuller tedarik eden güçlü bir üretim tesisine dönüşmüştür. Aydoğan\'ın kalite, hijyen ve sürdürülebilir üretim vizyonu, Sidem Gıda\'yı sektörde tercih edilen bir çözüm ortağı yapmıştır.',
    'about.founder.story.p4': 'Girişimcilik yolculuğu boyunca marka geliştirme, operasyon yönetimi, ürün tasarımı ve müşteri deneyimi gibi alanlarda yenilikçi bakış açısını koruyan Aydoğan; gıda, kahve, oyunlaştırma ve teknolojiyi bir araya getiren projeleriyle sektörde fark yaratmaya devam etmektedir.',
    'about.founder.story.p5': 'Bugün Ezgican Aydoğan, hem Sidem Gıda hem de Coffeetainer markalarını büyütmeye devam ederken; aynı zamanda geleceğin tüketici alışkanlıklarına yön verecek konsept mağazacılık, dijitalleşme ve yaratıcı girişimler üzerinde çalışmaktadır.',
    'about.founder.quote': 'İnovasyon ve kalite, başarılı bir işletmenin temel taşlarıdır. Her gün daha iyisini yaratmak için çalışıyoruz.',
    
    // About Page - Values Section
    'about.values.title': 'Temel Değerlerimiz',
    'about.values.subtitle': 'Yaptığımız her şeye rehberlik eden ilkeler',
    'about.values.quality.title': 'Önce Kalite',
    'about.values.quality.description': 'Malzeme kalitesi veya üretim standartlarından asla taviz vermiyoruz. Her ürün, mükemmellik taahhüdümüzü yansıtıyor.',
    'about.values.partnership.title': 'Ortaklık',
    'about.values.partnership.description': 'Sizin başarınız bizim başarımızdır. Güven, güvenilirlik ve karşılıklı büyümeye dayalı uzun vadeli ilişkiler kuruyoruz.',
    'about.values.consistency.title': 'Tutarlılık',
    'about.values.consistency.description': 'İlk siparişten bininciye kadar, her zaman aynı premium kalite ve güvenilir teslimatı bekleyebilirsiniz.',
    'about.values.innovation.title': 'İnovasyon',
    'about.values.innovation.description': 'Geleneği onurlandırırken, size daha iyi hizmet vermek için modern teknikleri ve sürdürülebilir uygulamaları benimsiyoruz.',
    
    // About Page - CTA Section
    'about.cta.title': 'Bizimle Ortaklık Kurmaya Hazır mısınız?',
    'about.cta.description': 'Fırın ihtiyaçları için Sidem Gıda\'ya güvenen yüzlerce işletmeye katılın. Başarınızı nasıl destekleyebileceğimizi görüşelim.',
    'about.cta.primaryButton': 'Toptan Satış Fiyatı Talep Et',
    'about.cta.secondaryButton': 'Ekibimizle İletişime Geç',
  },
  en: {
    // Navigation
    'nav.home': 'Home',
    'nav.about': 'About',
    'nav.products': 'Products',
    'nav.production': 'Production',
    'nav.wholesale': 'Wholesale',
    'nav.contact': 'Contact',
    'nav.contactUs': 'Contact Us',
    'nav.coffeetainer': 'Coffeetainer',
    
    // Common
    'common.learnMore': 'Learn More',
    'common.getStarted': 'Get Started',
    'common.readMore': 'Read More',
    'common.viewAll': 'View All',
    'common.submit': 'Submit',
    'common.cancel': 'Cancel',
    'common.search': 'Search',
    'common.filter': 'Filter',
    'common.sort': 'Sort',
    
    // Homepage - Hero Section
    'home.hero.slide1.title': 'Premium Quality at Wholesale Scale',
    'home.hero.slide1.subtitle': 'Your trusted bakery partner since 2016',
    'home.hero.slide1.cta': 'Explore Products',
    'home.hero.slide2.title': 'Where Tradition Meets Efficiency',
    'home.hero.slide2.subtitle': 'Artisanal quality for commercial success',
    'home.hero.slide2.cta': 'View Production',
    'home.hero.slide3.title': 'Bulk Orders Made Simple',
    'home.hero.slide3.subtitle': 'Streamlined wholesale ordering for your business',
    'home.hero.slide3.cta': 'Order Wholesale',
    
    // Homepage - Trust Indicators
    'home.trust.years': 'Years of Excellence',
    'home.trust.partners': 'Business Partners',
    'home.trust.deliveries': 'Monthly Deliveries',
    'home.trust.guaranteed': 'Quality Guaranteed',
    
    // Homepage - Quick Access
    'home.quickAccess.title': 'Your Gateway to Quality',
    'home.quickAccess.subtitle': 'Discover how Sidem Bakery can elevate your business with premium wholesale products',
    'home.quickAccess.products': 'Our Products',
    'home.quickAccess.products.desc': 'Explore our premium bakery product range',
    'home.quickAccess.production': 'Production Facility',
    'home.quickAccess.production.desc': 'See our state-of-the-art production process',
    'home.quickAccess.wholesale': 'Wholesale Orders',
    'home.quickAccess.wholesale.desc': 'Place bulk orders for your business',
    'home.quickAccess.about': 'About Sidem',
    'home.quickAccess.about.desc': 'Learn about our heritage and values',
    
    // Homepage - Featured Products
    'home.featured.title': 'Featured Products',
    'home.featured.subtitle': 'Discover our seasonal offerings and bestsellers',
    'home.featured.product1.name': 'Artisan Sourdough Bread',
    'home.featured.product1.category': 'Breads',
    'home.featured.product1.desc': 'Traditional sourdough with 48-hour fermentation',
    'home.featured.product1.badge': 'Bestseller',
    'home.featured.product2.name': 'Butter Croissants',
    'home.featured.product2.category': 'Pastries',
    'home.featured.product2.desc': 'Layered perfection with premium French butter',
    'home.featured.product2.badge': 'New',
    'home.featured.product3.name': 'Whole Grain Rolls',
    'home.featured.product3.category': 'Breads',
    'home.featured.product3.desc': 'Nutritious rolls perfect for restaurants',
    'home.featured.product3.badge': 'Seasonal',
    'home.featured.product4.name': 'Turkish Simit',
    'home.featured.product4.category': 'Specialty',
    'home.featured.product4.desc': 'Authentic sesame-crusted Turkish bagels',
    'home.featured.product4.badge': 'Traditional',
    'home.featured.viewAll': 'View All Products',
    
    // Homepage - Testimonials
    'home.testimonials.title': 'What Our Partners Say',
    'home.testimonials.subtitle': 'Trusted by businesses across Turkey',
    'home.testimonials.client1.quote': 'Sidem Bakery has been our trusted partner for 5 years. Their consistent quality and reliable delivery have helped our café maintain its reputation for excellence.',
    'home.testimonials.client1.name': 'Mehmet Yılmaz',
    'home.testimonials.client1.position': 'Owner',
    'home.testimonials.client1.company': 'Café Istanbul',
    'home.testimonials.client2.quote': 'The wholesale ordering process is seamless, and the product quality exceeds our expectations. Our guests consistently praise our breakfast offerings.',
    'home.testimonials.client2.name': 'Ayşe Demir',
    'home.testimonials.client2.position': 'Procurement Manager',
    'home.testimonials.client2.company': 'Grand Hotel Ankara',
    'home.testimonials.client3.quote': 'As a chef, I demand the best ingredients. Sidem delivers artisanal quality at wholesale scale—exactly what we need for our fine dining establishment.',
    'home.testimonials.client3.name': 'Can Özdemir',
    'home.testimonials.client3.position': 'Head Chef',
    'home.testimonials.client3.company': 'Restaurant Bosphorus',
    
    // Homepage - CTA Section
    'home.cta.title': 'Ready to Partner with Sidem Bakery?',
    'home.cta.subtitle': 'Join hundreds of businesses that trust us for their wholesale bakery needs',
    'home.cta.wholesale': 'Place Wholesale Order',
    'home.cta.contact': 'Contact Us',
    'home.cta.quality': 'Premium Quality',
    'home.cta.delivery': 'Reliable Delivery',
    'home.cta.pricing': 'Competitive Pricing',
    
    // Homepage - Footer
    'footer.company.title': 'Company',
    'footer.products.title': 'Products',
    'footer.contact.title': 'Contact Info',
    'footer.about': 'About Us',
    'footer.production': 'Production',
    'footer.contact': 'Contact',
    'footer.allProducts': 'All Products',
    'footer.wholesale': 'Wholesale',
    'footer.location': 'Istanbul, Turkey',
    'footer.phone': '+90 (212) 555-0123',
    'footer.email': 'info@sidembakery.com',
    'footer.description': 'Premium wholesale bakery supplier since 2016. Where tradition meets efficiency.',
    'footer.rights': 'All rights reserved.',
    'footer.privacy': 'Privacy Policy',
    'footer.terms': 'Terms of Service',
    
    // About/Production Footer
    'footer.section.company': 'Company',
    'footer.section.support': 'Support',
    'footer.section.legal': 'Legal',
    'footer.link.aboutUs': 'About Us',
    'footer.link.products': 'Products',
    'footer.link.production': 'Production',
    'footer.link.wholesale': 'Wholesale',
    'footer.link.contactUs': 'Contact Us',
    'footer.link.faq': 'FAQ',
    'footer.link.orderTracking': 'Order Tracking',
    'footer.link.privacyPolicy': 'Privacy Policy',
    'footer.link.termsOfService': 'Terms of Service',
    'footer.link.qualityStandards': 'Quality Standards',
    'footer.tagline': 'Premium wholesale bakery supplier since 2016. Where tradition meets efficiency.',
    'footer.allRights': 'All rights reserved.',
    'footer.craftedIn': 'Crafted with care in Turkey',
    
    // Production Page
    'production.content.paragraph1': 'At Sidem Gıda, we build our production processes on the principles of freshness, hygiene, quality, taste, and advanced technology. The latest equipment used in our modern production line raises standards at every stage, while the professionalism of our experienced craftsmen adds a unique character to our products.',
    'production.content.paragraph2': 'All steps from raw material selection to baking and final inspection are meticulously monitored. We work in accordance with food safety protocols in our hygienic production areas, ensuring that each product meets the same high standards. By combining our craftsmen\'s years of experience with technology, we consistently provide our customers with fresh, reliable, and deliciously distinctive flour-based products.',
    'production.content.paragraph3': 'Sidem Gıda draws its production power, which supplies Turkey\'s leading brands, from its passion for quality and continuously modernizing facilities.',
    
    // Contact Page
    'contact.hero.title': 'Contact Us',
    'contact.hero.subtitle': 'We are always here for your questions, requests and suggestions',
    'contact.info.phone': 'Phone',
    'contact.info.email': 'Email',
    'contact.info.address': 'Address',
    'contact.info.whatsapp': 'WhatsApp',
    'contact.info.whatsapp.support': 'For quick support',
    'contact.form.title': 'Send Us a Message',
    'contact.form.subtitle': 'Fill out the form and we\'ll get back to you as soon as possible',
    'contact.form.type': 'Request Type',
    'contact.form.type.placeholder': 'Please select',
    'contact.form.type.wholesale': 'Wholesale Sales Request',
    'contact.form.type.partnership': 'Partnership and Collaboration',
    'contact.form.type.technical': 'Technical Support',
    'contact.form.type.general': 'General Information',
    'contact.form.type.complaint': 'Complaint and Suggestion',
    'contact.form.type.quality': 'Quality and Certification',
    'contact.form.name': 'Full Name',
    'contact.form.email': 'Email',
    'contact.form.phone': 'Phone',
    'contact.form.company': 'Company Name',
    'contact.form.message': 'Your Message',
    'contact.form.submit': 'Send Message',
    'contact.map.title': 'COFFEETAINER',
    'contact.map.viewOnMaps': 'Open in Google Maps',
    'contact.map.directions': 'Get Directions',
    'contact.hours.title': 'Business Hours',
    'contact.hours.monday': 'Monday',
    'contact.hours.tuesday': 'Tuesday',
    'contact.hours.wednesday': 'Wednesday',
    'contact.hours.thursday': 'Thursday',
    'contact.hours.friday': 'Friday',
    'contact.hours.saturday': 'Saturday',
    'contact.hours.sunday': 'Sunday',
    'contact.hours.closed': 'Closed',
    
    // Wholesale Form
    'wholesale.form.title': 'Request a Wholesale Quote',
    'wholesale.form.subtitle': 'Fill out the form below and our team will contact you within 24 hours',
    'wholesale.form.companyName': 'Company Name',
    'wholesale.form.companyName.placeholder': 'Your Company Name',
    'wholesale.form.contactPerson': 'Contact Person',
    'wholesale.form.contactPerson.placeholder': 'Full Name',
    'wholesale.form.email': 'Email Address',
    'wholesale.form.email.placeholder': 'company@example.com',
    'wholesale.form.phone': 'Phone Number',
    'wholesale.form.phone.placeholder': '+90 XXX XXX XX XX',
    'wholesale.form.businessType': 'Business Type',
    'wholesale.form.businessType.placeholder': 'Select your business type',
    'wholesale.form.businessType.restaurant': 'Restaurant',
    'wholesale.form.businessType.cafe': 'Cafe/Coffee Shop',
    'wholesale.form.businessType.hotel': 'Hotel',
    'wholesale.form.businessType.catering': 'Catering Company',
    'wholesale.form.businessType.bakery': 'Bakery/Pastry Shop',
    'wholesale.form.businessType.distributor': 'Food Distributor',
    'wholesale.form.businessType.supermarket': 'Supermarket Chain',
    'wholesale.form.businessType.other': 'Other',
    'wholesale.form.productType': 'Product Type',
    'wholesale.form.productType.placeholder': 'Select product type',
    'wholesale.form.productType.coffee': 'Coffee',
    'wholesale.form.productType.bakery': 'Baked Goods',
    'wholesale.form.productInterest': 'Product Interest',
    'wholesale.form.productInterest.loading': 'Loading products...',
    'wholesale.form.productInterest.none': 'No products found for this category',
    'wholesale.form.estimatedQuantity': 'Estimated Quantity',
    'wholesale.form.estimatedQuantity.placeholder': 'Select quantity range',
    'wholesale.form.unit.bakery': 'unit',
    'wholesale.form.unit.coffee': 'kg',
    'wholesale.form.deliveryFrequency': 'Delivery Frequency',
    'wholesale.form.deliveryFrequency.placeholder': 'Select delivery frequency',
    'wholesale.form.deliveryFrequency.daily': 'Daily',
    'wholesale.form.deliveryFrequency.every2-3days': 'Every 2-3 days',
    'wholesale.form.deliveryFrequency.weekly': 'Weekly',
    'wholesale.form.deliveryFrequency.biweekly': 'Bi-weekly',
    'wholesale.form.deliveryFrequency.monthly': 'Monthly',
    'wholesale.form.preferredCurrency': 'Preferred Currency',
    'wholesale.form.currency.try': 'Turkish Lira (₺)',
    'wholesale.form.currency.eur': 'Euro (€)',
    'wholesale.form.currency.usd': 'US Dollar ($)',
    'wholesale.form.message': 'Additional Information',
    'wholesale.form.message.placeholder': 'Tell us more about your requirements, special requests, or any questions you have...',
    'wholesale.form.required': 'Required fields. We respect your privacy and will never share your information.',
    'wholesale.form.submit': 'Submit Wholesale Request',
    'wholesale.form.submitting': 'Submitting Request...',
    'wholesale.form.success.title': 'Your Request Has Been Sent Successfully!',
    'wholesale.form.success.thankyou': 'Thank you for your interest.',
    'wholesale.form.success.message': 'We will get back to you as soon as possible. Our wholesale team will review your request and contact you.',
    'wholesale.form.error.companyName': 'Company name is required',
    'wholesale.form.error.contactPerson': 'Contact person name is required',
    'wholesale.form.error.email.required': 'Email is required',
    'wholesale.form.error.email.invalid': 'Please enter a valid email address',
    'wholesale.form.error.phone.required': 'Phone number is required',
    'wholesale.form.error.phone.invalid': 'Please enter a valid phone number',
    'wholesale.form.error.businessType': 'Please select your business type',
    'wholesale.form.error.productType': 'Please select product type',
    'wholesale.form.error.productInterest': 'Please select at least one product',
    'wholesale.form.error.estimatedQuantity': 'Please select estimated quantity',
    'wholesale.form.error.deliveryFrequency': 'Please select delivery frequency',
    
    // Wholesale Page - Hero Section
    'wholesale.hero.title': 'Wholesale Partnership Program',
    'wholesale.hero.subtitle': 'Premium quality at wholesale scale. Partner with Sidem Bakery for reliable supply, competitive pricing, and exceptional service since 2016.',
    
    // Wholesale Page - Benefits Section
    'wholesale.benefits.title': 'Why Choose Sidem for Wholesale?',
    'wholesale.benefits.subtitle': 'Premium quality at wholesale scale with trusted service since 2016',
    'wholesale.benefits.quality.title': 'Premium Quality',
    'wholesale.benefits.quality.description': 'Consistent artisan quality across all wholesale volumes with strict quality control standards.',
    'wholesale.benefits.delivery.title': 'Reliable Delivery',
    'wholesale.benefits.delivery.description': 'Flexible delivery schedules from daily to monthly, ensuring your business never runs out of fresh products.',
    'wholesale.benefits.pricing.title': 'Competitive Pricing',
    'wholesale.benefits.pricing.description': 'Transparent pricing in Turkish Lira or Euro with volume-based discounts up to 25%.',
    'wholesale.benefits.support.title': 'Dedicated Support',
    'wholesale.benefits.support.description': 'Personal account manager and 24/7 customer support to manage all your wholesale needs efficiently.',
    
    // Wholesale Page - FAQ Section
    'wholesale.faq.title': 'Frequently Asked Questions',
    'wholesale.faq.subtitle': 'Find answers to common questions about our wholesale services',
    'wholesale.faq.q1.question': 'What is the minimum order quantity for wholesale?',
    'wholesale.faq.q1.answer': 'Our minimum order quantity starts at 100 units per week for the Starter package. However, we are flexible and can arrange custom setups based on your specific business needs and growth plans.',
    'wholesale.faq.q2.question': 'How quickly can you deliver orders?',
    'wholesale.faq.q2.answer': 'Delivery schedules range from daily to monthly depending on your chosen package and location. For Istanbul and surrounding areas, we offer same-day delivery for orders placed before 10 AM. Other regions typically receive delivery within 24-48 hours.',
    'wholesale.faq.q3.question': 'Do you offer custom product development?',
    'wholesale.faq.q3.answer': 'Yes! Our Enterprise package includes custom product development services. We work closely with your team to create unique bakery products that match your brand requirements and customer preferences.',
    'wholesale.faq.q4.question': 'What payment terms do you offer?',
    'wholesale.faq.q4.answer': 'Payment terms vary by package: 30 days for Starter, 45 days for Professional, and 60 days for Enterprise. We accept bank transfer, credit card, and can arrange custom payment schedules for long-term partners.',
    'wholesale.faq.q5.question': 'Can I get samples before committing to a wholesale order?',
    'wholesale.faq.q5.answer': 'Absolutely! We encourage all potential wholesale partners to try our products first. Contact our sales team to arrange a complimentary sample package of our most popular items.',
    'wholesale.faq.q6.question': 'How do you handle product quality issues?',
    'wholesale.faq.q6.answer': 'We have a 100% satisfaction guarantee. If any product doesn\'t meet our quality standards, we will replace it immediately at no charge. Our quality assurance team conducts regular checks throughout the production and delivery process.',
    
    // Wholesale Page - CTA Section
    'wholesale.cta.title': 'Ready to Partner with Sidem Bakery?',
    'wholesale.cta.subtitle': 'Join hundreds of satisfied wholesale customers who trust us for premium quality bakery products. Let\'s discuss how we can support your business growth.',
    'wholesale.cta.contactSales': 'Contact Sales Team',
    'wholesale.cta.viewCatalog': 'View Product Catalog',
    
    // About Page - Hero Section
    'about.hero.title': 'Where Tradition Meets Excellence',
    'about.hero.subtitle': 'Our Story',
    'about.hero.description': 'Sidem Gıda has been serving the flour products sector since 2016 with a boutique production approach that combines quality, hygiene, and taste. We offer high-standard solutions to businesses, particularly with French croissants requiring mastery, specialty recipe brioche, fresh daily pastries, cookies, savory pastries, and confectionery products. We prepare each product with traditional techniques, proper dough resting periods, and selected raw materials, ensuring both freshness and sustainable quality assurance for our customers. As Sidem Gıda, we produce customized products according to business needs and continue to be a reliable supplier for professional kitchens in many parts of Turkey with our wholesale sales.',
    
    // About Page - Timeline Section
    'about.timeline.title': 'Our Journey',
    'about.timeline.subtitle': 'The milestones that shaped our commitment to excellence',
    'about.timeline.2016.title': 'Foundation',
    'about.timeline.2016.description': 'Sidem Gıda was founded with a small production facility and commitment to quality, with a vision to bring traditional Turkish bakery excellence to the wholesale market.',
    'about.timeline.2018.title': 'Expansion',
    'about.timeline.2018.description': 'We increased our production capacity by 300% and introduced automated quality control systems while maintaining our artisanal approach.',
    'about.timeline.2020.title': 'Factory Transformation',
    'about.timeline.2020.description': 'After establishing the Coffeetainer brand in 2016, our production workshop primarily serving our own chain branches quickly transformed into a factory with increasing demand, our quality standards, and professional production power, becoming a strong supplier providing wholesale sales to Turkey\'s leading brands today.',
    'about.timeline.2022.title': 'Innovation',
    'about.timeline.2022.description': 'We launched our digital wholesale platform and introduced sustainable packaging solutions, making ordering easier while protecting the environment.',
    'about.timeline.2024.title': 'Regional Leader',
    'about.timeline.2024.description': 'We became the preferred wholesale bakery supplier for businesses, serving restaurants, hotels, and cafes throughout Turkey with consistent excellence.',
    
    // About Page - Founder Section
    'about.founder.title': 'Our Founder',
    'about.founder.name': 'Ezgican Aydoğan',
    'about.founder.position': 'Founder & Entrepreneur',
    'about.founder.story.p1': 'Ezgican Aydoğan is the founder of Coffeetainer and Sidem Gıda, two of Turkey\'s innovative brands, with many years of experience in the horeca and food industry. Starting his career in the hospitality sector, Aydoğan combined his experience in guest experience, gastronomy management, and operational processes with his entrepreneurial vision to create his own brands.',
    'about.founder.story.p2': 'Coffeetainer, which he founded in 2016, quickly reached a wide audience by combining third-generation coffee culture with container architecture and modern design approach, positioning itself as a strong brand with branches in many cities such as Antalya, Ankara, Manisa, and Baku. Thanks to Aydoğan\'s design-oriented approach, Coffeetainer has become not just a coffee chain, but also a lifestyle and experience brand.',
    'about.founder.story.p3': 'Sidem Gıda, established in parallel with Coffeetainer\'s growth, started as a workshop providing flour product production for the brand\'s own branches but quickly transformed into a powerful production facility supplying wholesale croissants and specialty flour products to Turkey\'s leading coffee chains and brands. Aydoğan\'s vision of quality, hygiene, and sustainable production has made Sidem Gıda a preferred solution partner in the industry.',
    'about.founder.story.p4': 'Throughout his entrepreneurial journey, Aydoğan maintains his innovative perspective in areas such as brand development, operations management, product design, and customer experience, continuing to make a difference in the industry with projects that combine food, coffee, gamification, and technology.',
    'about.founder.story.p5': 'Today, Ezgican Aydoğan continues to grow both Sidem Gıda and Coffeetainer brands while working on concept retail, digitalization, and creative ventures that will shape future consumer habits.',
    'about.founder.quote': 'Innovation and quality are the cornerstones of a successful business. We work to create something better every day.',
    
    // About Page - Values Section
    'about.values.title': 'Our Core Values',
    'about.values.subtitle': 'The principles that guide everything we do',
    'about.values.quality.title': 'Quality First',
    'about.values.quality.description': 'We never compromise on ingredient quality or production standards. Every product reflects our commitment to excellence.',
    'about.values.partnership.title': 'Partnership',
    'about.values.partnership.description': 'Your success is our success. We build long-term relationships based on trust, reliability, and mutual growth.',
    'about.values.consistency.title': 'Consistency',
    'about.values.consistency.description': 'From the first order to the thousandth, you can always expect the same premium quality and reliable delivery.',
    'about.values.innovation.title': 'Innovation',
    'about.values.innovation.description': 'While honoring tradition, we embrace modern techniques and sustainable practices to better serve you.',
    
    // About Page - CTA Section
    'about.cta.title': 'Ready to Partner with Us?',
    'about.cta.description': 'Join hundreds of businesses that trust Sidem Gıda for their bakery needs. Let\'s discuss how we can support your success.',
    'about.cta.primaryButton': 'Request Wholesale Pricing',
    'about.cta.secondaryButton': 'Contact Our Team',
  },
};

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguageState] = useState<Language>('tr');

  useEffect(() => {
    // Load language from localStorage or default to Turkish
    const savedLanguage = localStorage.getItem('language') as Language;
    if (savedLanguage && (savedLanguage === 'tr' || savedLanguage === 'en')) {
      setLanguageState(savedLanguage);
    } else {
      setLanguageState('tr'); // Default to Turkish
      localStorage.setItem('language', 'tr');
    }
  }, []);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('language', lang);
  };

  const t = (key: string): string => {
    return translations[language][key] || key;
  };

  return (
    <LanguageContext.Provider data-component-id="src/contexts/LanguageContext.tsx:688:4" data-component-path="src/contexts/LanguageContext.tsx" data-component-line="688" data-component-file="LanguageContext.tsx" data-component-name="LanguageContext.Provider" data-component-content="%7B%22elementName%22%3A%22LanguageContext.Provider%22%7D" value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};